import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "@/providers/AuthenticationProvider";
import { APP_CONFIG_REGISTRY } from "@/constants/appConfigRegistry";
import { fetchAppConfig, hydrateAppConfig } from "@/redux/config/configSlice";
import { readSessionCache, SESSION_CACHE_KEYS, subscribeSessionCache, writeSessionCache } from "@/utilities/auth/sessionCache";
import type { AppDispatch } from "@/redux/store";

export function AppConfigWrapper({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const { accessToken, apiEnv, userProfile } = useAuth();
  const userId = userProfile?.userId ?? "";
  const hasFetched = useRef(false);

  // Serve cached configs right away, then refetch once per tab and share the result with other tabs.
  useEffect(() => {
    if (!accessToken || !apiEnv || !userProfile || hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    APP_CONFIG_REGISTRY.forEach(({ name, version }) => {
      const cacheKey = SESSION_CACHE_KEYS.appConfig(name, version);
      const cached = readSessionCache<unknown>(cacheKey, userId, apiEnv);
      if (cached !== null) {
        dispatch(hydrateAppConfig({ configName: name, data: cached }));
      }

      dispatch(fetchAppConfig({ apiEnv, accessToken, configName: name, version }))
        .unwrap()
        .then(({ data }) => writeSessionCache(cacheKey, userId, apiEnv, data))
        .catch(() => {
          // Failure state is already recorded in the slice.
        });
    });
  }, [accessToken, apiEnv, userProfile, userId, dispatch]);

  // Pick up configs refreshed by other tabs.
  useEffect(() => {
    if (!apiEnv || !userId) {
      return;
    }
    const unsubscribers = APP_CONFIG_REGISTRY.map(({ name, version }) =>
      subscribeSessionCache<unknown>(SESSION_CACHE_KEYS.appConfig(name, version), userId, apiEnv, (data) =>
        dispatch(hydrateAppConfig({ configName: name, data }))
      )
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [apiEnv, userId, dispatch]);

  return <>{children}</>;
}
